import React, { useEffect, useMemo, useState } from "react";
import { API } from "../../../config/Config";
import { useAppData } from "../../../context/AppDataContext";
import { axiosInstance } from "../../../bricks/axios";
import Loading from "../../../bricks/Loading";
import QuestionBar from "./QuestionBar";

import "./TestResultOverview.scss";

const normalize = (s) =>
    (s ?? "")
        .toString()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase()
        .trim();

const countPlaceholders = (qText) => (qText?.match(/%s/g) || []).length;

const getAcceptedMapForOpen = (q) => {
    const candidate = q.acceptedAnswers || q.answerMap;
    if (candidate && typeof candidate === "object") {
        const m = new Map();
        for (const [k, arr] of Object.entries(candidate)) {
            const idx = Number(k);
            if (Array.isArray(arr) && arr.length) {
                m.set(idx, new Set(arr.map(normalize)));
            }
        }
        if (m.size) return m;
    }
    if (Array.isArray(q.answerList) && q.answerList.length) {
        const hasIndex = q.answerList.some((a) => typeof a.index === "number");
        if (hasIndex) {
            const m = new Map();
            for (const a of q.answerList) {
                const idx = Number(a.index);
                if (!m.has(idx)) m.set(idx, new Set());
                m.get(idx).add(normalize(a.value));
            }
            if (m.size) return m;
        } else if (countPlaceholders(q.question) === 1) {
            const m = new Map();
            m.set(0, new Set(q.answerList.map((a) => normalize(a.value))));
            return m;
        }
    }
    return null;
};

// formátování času hh:mm:ss
const formatDuration = (ms) => {
    if (!Number.isFinite(ms) || ms <= 0) return "—";
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${h}h ${pad(m)}m ${pad(s)}s` : `${m}m ${pad(s)}s`;
};

const TestResultOverview = () => {
    const { setError, selectedPeriod, period, getSortingTestQuestionsList } =
        useAppData();

    const [sortingTestQuestionList, setSortingTestQuestionList] = useState([]);
    const [testQuestionResultList, setTestQuestionResultList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTestIndex, setSelectedTestIndex] = useState();

    // načtení dat (otázky + odpovědi) pro zvolený period
    useEffect(() => {
        const loadAll = async () => {
            if (!selectedPeriod) return;
            try {
                setIsLoading(true);

                const [questions, results] = await Promise.all([
                    getSortingTestQuestionsList(),
                    (async () => {
                        const { method, url } = API.getTestQuestionResultListByPeriod(
                            selectedPeriod
                        );
                        const { data } = await axiosInstance[method](url);
                        return data || [];
                    })(),
                ]);

                setSortingTestQuestionList(questions || []);
                setTestQuestionResultList(results || []);

                if (period?.sortingTestDefinitionList?.length) {
                    setSelectedTestIndex(Number(period.sortingTestDefinitionList[0].order));
                }
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        };
        loadAll();
    }, [selectedPeriod, period, getSortingTestQuestionsList, setError]);

    const testIndexOptions = useMemo(() => {
        return (period?.sortingTestDefinitionList || []).map((d) => ({
            value: Number(d.order),
            label: `Test #${d.order}`,
        }));
    }, [period]);

    const questionsForSelected = useMemo(() => {
        if (!selectedTestIndex) return [];
        return (sortingTestQuestionList || []).filter(
            (q) => Number(q.test) === Number(selectedTestIndex)
        );
    }, [sortingTestQuestionList, selectedTestIndex]);

    const resultsForSelected = useMemo(() => {
        if (!selectedTestIndex) return [];
        return (testQuestionResultList || []).filter(
            (r) => Number(r.testIndex) === Number(selectedTestIndex)
        );
    }, [testQuestionResultList, selectedTestIndex]);

    // agregace: počty správně/špatně (MCQ i OPEN) + answered fallback
    const perQuestionStats = useMemo(() => {
        const map = new Map();
        for (const q of questionsForSelected) {
            map.set(q._id, {
                questionId: q._id,
                question: q.question,
                type: q.type, // 0 = MCQ, 1 = OPEN
                correct: 0,
                wrong: 0,
                answered: 0,
            });
        }

        for (const doc of resultsForSelected) {
            const aMap = doc.answerMap || {};
            for (const q of questionsForSelected) {
                const entry = map.get(q._id);
                if (!entry) continue;

                const ans = aMap[q._id]?.value;
                if (ans == null) continue;

                if (q.type === 0) {
                    // MCQ
                    const picked = (q.answerList || []).find((o) => o._id === ans);
                    if (!picked) entry.wrong += 1;
                    else if (picked.evaluation) entry.correct += 1;
                    else entry.wrong += 1;
                    continue;
                }

                if (q.type === 1) {
                    // OPEN
                    const needed = countPlaceholders(q.question);
                    if (needed === 0) {
                        entry.answered += 1;
                        continue;
                    }

                    const accepted = getAcceptedMapForOpen(q);
                    if (!accepted) {
                        // bez rubriky – bereme jako „answered“, pokud jsou vyplněné všechny boxy
                        let allFilled = true;
                        for (let i = 0; i < needed; i++) {
                            const v = ans?.[i];
                            if (!v || String(v).trim() === "") {
                                allFilled = false;
                                break;
                            }
                        }
                        if (allFilled) entry.answered += 1;
                        continue;
                    }

                    // s rubrikou – všechny boxy musí být správně
                    let allCorrect = true;
                    for (let i = 0; i < needed; i++) {
                        const userVal = normalize(ans?.[i] ?? "");
                        const okSet = accepted.get(i) || new Set();
                        if (!userVal || !okSet.has(userVal)) {
                            allCorrect = false;
                            break;
                        }
                    }
                    if (allCorrect) entry.correct += 1;
                    else entry.wrong += 1;
                    continue;
                }

                // ostatní typy – jen answered
                entry.answered += 1;
            }
        }

        return Array.from(map.values());
    }, [questionsForSelected, resultsForSelected]);

    // průměrný čas vyplňování
    const avgDurationMs = useMemo(() => {
        if (!resultsForSelected.length) return null;
        let sum = 0,
            n = 0;
        for (const r of resultsForSelected) {
            const start = r.start ? new Date(r.start).getTime() : NaN;
            const endLike = r.end
                ? new Date(r.end).getTime()
                : r.updatedAt
                    ? new Date(r.updatedAt).getTime()
                    : NaN;
            if (Number.isFinite(start) && Number.isFinite(endLike)) {
                const diff = endLike - start;
                if (diff > 0) {
                    sum += diff;
                    n += 1;
                }
            }
        }
        return n > 0 ? Math.round(sum / n) : null;
    }, [resultsForSelected]);

    const avgSampleCount = useMemo(() => {
        if (!avgDurationMs) return 0;
        let n = 0;
        for (const r of resultsForSelected) {
            const start = r.start ? new Date(r.start).getTime() : NaN;
            const endLike = r.end
                ? new Date(r.end).getTime()
                : r.updatedAt
                    ? new Date(r.updatedAt).getTime()
                    : NaN;
            if (Number.isFinite(start) && Number.isFinite(endLike) && endLike - start > 0)
                n += 1;
        }
        return n;
    }, [resultsForSelected, avgDurationMs]);

    if (isLoading) return <Loading />;

    return (
        <div className="TestResultOverview">
            <div className="controls">
                <label>
                    Vyberte test:&nbsp;
                    <select
                        value={selectedTestIndex ?? ""}
                        onChange={(e) => setSelectedTestIndex(Number(e.target.value) || undefined)}
                    >
                        {(period?.sortingTestDefinitionList || []).map((d) => (
                            <option key={d._id} value={d.order}>{`Test #${d.order}`}</option>
                        ))}
                    </select>
                </label>

                <div className="avgTime">
          <span>
            Průměrný čas: <strong>{formatDuration(avgDurationMs)}</strong>
          </span>
                    {avgSampleCount ? (
                        <span className="muted">&nbsp;(n={avgSampleCount})</span>
                    ) : null}
                </div>
            </div>

            {!selectedTestIndex ? (
                <div className="placeholder">Vyberte test pro zobrazení statistik.</div>
            ) : (
                <div className="qgrid">
                    {perQuestionStats.map((s, i) => (
                        <QuestionBar key={s.questionId} s={s} index={i} />
                    ))}
                    {!perQuestionStats.length && (
                        <div className="placeholder">
                            Pro vybraný test nejsou k dispozici žádné otázky.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TestResultOverview;
